import type { Ref } from 'vue';
import type { RelatedItemState } from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import { computed, ref } from 'vue';

/**
 * Staged connect/disconnect state for a relation-assignment field. Tracks pending additions and
 * removals against the currently-effective ids, derives the effective set and per-item display
 * state, and exposes whether anything is staged. The caller owns the queries, the save, and the
 * entitlement gate; this only models the pending mutations.
 */
export function usePendingConnections(currentIds: Ref<string[]>) {
	const pendingConnections = ref(new Set<string>());
	const pendingDisconnections = ref(new Set<string>());

	const effectiveIds = computed(() => new Set(currentIds.value)
		.difference(pendingDisconnections.value)
		.union(pendingConnections.value),
	);

	const hasChanges = computed(() => pendingConnections.value.size > 0 || pendingDisconnections.value.size > 0);

	// Re-connecting a currently-effective id cancels a staged removal; otherwise stage an addition.
	function connect(id: string) {
		if (currentIds.value.includes(id)) {
			pendingDisconnections.value.delete(id);
		}
		else {
			pendingConnections.value.add(id);
		}
	}

	// Removing a not-yet-saved addition just drops it; otherwise stage a removal.
	function disconnect(id: string) {
		if (pendingConnections.value.has(id)) {
			pendingConnections.value.delete(id);
		}
		else {
			pendingDisconnections.value.add(id);
		}
	}

	function undo(id: string) {
		pendingConnections.value.delete(id);
		pendingDisconnections.value.delete(id);
	}

	function getItemState(id: string): RelatedItemState {
		if (pendingConnections.value.has(id)) return 'connected';
		if (pendingDisconnections.value.has(id)) return 'disconnected';
		return 'default';
	}

	function clearConnections() {
		pendingConnections.value = new Set();
	}

	function clearDisconnections() {
		pendingDisconnections.value = new Set();
	}

	return {
		pendingConnections,
		pendingDisconnections,
		effectiveIds,
		hasChanges,
		connect,
		disconnect,
		undo,
		getItemState,
		clearConnections,
		clearDisconnections,
	};
}
